import { useMemo } from "react";
import { IntlShape, MessageDescriptor, useIntl } from "react-intl";
import { ExtraCategories, ExtraCategory } from "../../domain/ExtraCategory.enum";
import { IssueCode, ValidationIssue } from "../../domain/entities/ValidationIssue";
import { VehicleType } from "../../domain/VehicleType";
import { issueMessages, noActiveExtraMessages } from "../../presentation/issues.messages";
import {
  categoryLabelMessages,
  categoryPluralMessages,
  vehicleTypeMessages,
} from "../../presentation/labels.messages";

/** Codes whose `{category}` reads as a plural noun ("how many vehicles"); the rest use the name. */
const PLURAL_CATEGORY_CODES: readonly string[] = [
  IssueCode.CATEGORY_LIMITS_INCLUDED_REQUIRED,
  IssueCode.CATEGORY_LIMITS_MAX_REQUIRED,
];

/**
 * Turns a `ValidationIssue` into the text shown to the user: the react-intl message of
 * its code, with category and vehicle type codes translated and names inserted as received, or
 * `default_message` when the code has no message.
 */
export class IssueFormatter {
  constructor(private readonly intl: IntlShape) {}

  format(issue: ValidationIssue): string {
    const message = this.messageOf(issue);
    if (!message) {
      return issue.defaultMessage;
    }
    return this.intl.formatMessage(message, this.valuesOf(issue));
  }

  categoryName(category: ExtraCategory): string {
    return this.intl.formatMessage(categoryLabelMessages[category]);
  }

  vehicleTypeName(vehicleType: VehicleType): string {
    return this.intl.formatMessage(vehicleTypeMessages[vehicleType.code]);
  }

  private messageOf(issue: ValidationIssue): MessageDescriptor | undefined {
    const code = issue.knownCode();
    if (code === undefined) {
      return undefined;
    }
    if (code === IssueCode.CATEGORY_LIMITS_NO_ACTIVE_EXTRA) {
      const category = issue.category();
      return category && category !== ExtraCategory.NONE
        ? noActiveExtraMessages[category]
        : undefined;
    }
    return issueMessages[code];
  }

  private valuesOf(issue: ValidationIssue): Record<string, string> {
    const values: Record<string, string> = { ...issue.params };
    const category = ExtraCategories.parse(issue.param("category"));
    if (category) {
      values.category = this.categoryText(issue, category);
    }
    const vehicleType = VehicleType.tryOf(issue.param("vehicleType"));
    if (vehicleType) {
      values.vehicleType = this.vehicleTypeName(vehicleType);
    }
    return values;
  }

  private categoryText(issue: ValidationIssue, category: ExtraCategory): string {
    if (category !== ExtraCategory.NONE && PLURAL_CATEGORY_CODES.includes(issue.code)) {
      return this.intl.formatMessage(categoryPluralMessages[category]);
    }
    return this.categoryName(category);
  }
}

export function useIssueFormatter(): IssueFormatter {
  const intl = useIntl();
  return useMemo(() => new IssueFormatter(intl), [intl]);
}
